/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { Link, useParams } from "@packages/framework";

import type { ThemeType } from "../../../../../global/emotion";
import {
  colors,
  fontSizes,
  fontWeights,
  lineHeights,
  numericPixels,
  spacing,
} from "../../../../../global/emotion";
import { Button } from "../../../_base/_core/Button/Button.emotion";
import { IconButton } from "../../../_base/_core/IconButton/IconButton.emotion";
import { Text } from "../../../_base/_core/Text/Text.emotion";
import type { IconElement } from "../../../_base/icons/Icon/Icon";

const containerStyle: Interpolation = {
  WebkitTapHighlightColor: "transparent",
  alignItems: "center",
  backgroundColor: colors.transparent,
  ":hover": { backgroundColor: "transparent" },
  ":focus": { backgroundColor: "transparent" },
  ":active": { backgroundColor: "transparent" },
  display: "flex",
  flexDirection: "column",
  maxWidth: numericPixels[64],
  padding: spacing.s,
  rowGap: spacing.none,
  width: numericPixels[64],
};
const iconButtonStyle: Interpolation = {
  padding: spacing.none,
};
const textStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.onOverlayHighest,
  fontSize: fontSizes.buttonXS,
  fontWeight: fontWeights.buttonXS,
  lineHeight: lineHeights.buttonXS,
  paddingTop: spacing.s,
  textWrap: "nowrap",
});

export type SocialIconButtonProps = {
  action?: string;
  isExternalIcon?: boolean;
  title: string;
  icon: IconElement;
  onClick?: () => void;
  type?: string;
  url?: string;
};

export const SocialIconButton = ({
  action,
  isExternalIcon = true,
  title,
  icon,
  onClick,
  type,
  url,
  ...props
}: SocialIconButtonProps) => {
  const analytics = useAnalytics();
  const params = useParams();

  const track = () => {
    if (type === "campaign") {
      analytics.track({
        event: HallowAnalyticsEvent.TappedShareCampaign,
        properties: {
          campaign: params.id,
          action: action,
          share_sheet: "campaign",
        },
      });
    }
  };

  const titledButton = (
    <>
      <IconButton
        asChild
        icon={icon}
        variant={isExternalIcon ? "secondary" : "neutral"}
        css={isExternalIcon ? iconButtonStyle : undefined}
      >
        <span />
      </IconButton>
      <Text css={textStyle}>{title}</Text>
    </>
  );

  return (
    <span onClick={() => track()}>
      {url ? (
        <Link href={url} target="_blank" css={containerStyle} {...props}>
          {titledButton}
        </Link>
      ) : (
        <Button
          onClick={onClick}
          css={containerStyle}
          {...props}
          size="m"
          variant="secondary"
        >
          {titledButton}
        </Button>
      )}
    </span>
  );
};
